import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

import Button from "@client/components/ui/Button";
import ButtonLoading from "@client/components/ui/ButtonLoading";
import InputField from "@client/components/ui/InputField";
import Modal from "@client/components/ui/Modal";
import licensorImage from "@client/components/ui/sharedImages/licensor.svg";
import licensorStore from "@client/stores/LicensorStore";
import notificationStore from "@client/stores/NotificationStore";
import roleStore from "@client/stores/RoleStore";
import Style from "@client/utils/Style";
import { InputField as InputFieldType } from "@typings/InputField";
import { Licensor, LicensorInput, LicensorWithRole } from "@typings/Licensor";

import inputFieldsSchema from "./InputFields";
import { Partner } from "@typings/Partner";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const Header = styled.div`
  display: flex;
  justify-content: center;
  margin: 48px 0;
`;
const LicensorImage = styled.div`
  ${Style.exactSize("96px")}
  background: url('${licensorImage}') center no-repeat;
`;
const Fields = styled.form`
  display: flex;
  flex-wrap: wrap;
  margin: 0 32px;
  justify-content: space-between;
  margin-top: -16px;
  > div {
    margin-top: 16px;
  }
`;
const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 48px 32px 32px 32px;
`;
type State = {
  inputFields: InputFieldType[];
  highlightedFields: string[];
};
type Props = {
  onRequestClose: () => void;
  licensor?: LicensorWithRole | null;
  partner?: Partner;
};

@observer
class LicensorModal extends React.Component<Props, State> {
  state: State = {
    inputFields: [],
    highlightedFields: [],
  };

  get saving(): boolean {
    return licensorStore.updating;
  }

  UNSAFE_componentWillMount() {
    let inputFields = [...inputFieldsSchema.FIELDS];
    const licensor = this.props.licensor;

    if (licensor) {
      inputFields = inputFields.map(f => {
        const value = licensor![f.name as keyof Licensor];
        if (value != null) {
          return { ...f, value };
        }
        return { ...f };
      });
    }

    this.setState({ inputFields });
    this.populateRoles();
  }

  async populateRoles() {
    await roleStore.load({ isPartner: this.props.partner != null });
    const roles = roleStore.roles;
    const roleField = this.state.inputFields.find(f => f.name === "role_name");
    if (roleField) {
      roleField.enum = roles.map(r => ({
        label: r,
        value: r,
      }));
      roleField.description = roles.find(r => r === "admin")
        ? inputFieldsSchema.ROLE_DESCRIPTION_ADMIN
        : inputFieldsSchema.ROLE_DESCRIPTION;
    }
    this.setState({ inputFields: [...this.state.inputFields] });
  }

  handleFieldChange(field: InputFieldType, value: any) {
    const inputFields = this.state.inputFields.map(f =>
      f.name === field.name ? { ...f, value } : { ...f },
    );
    this.setState({ inputFields });
  }

  async handleSaveClick() {
    const messages: string[] = [];
    const highlightedFields = this.state.inputFields
      .filter(f => {
        const validateFunc = f.validate;
        if (validateFunc) {
          const result = validateFunc(f, this.state.inputFields);
          if (typeof result === "string") {
            messages.push(result);
            return true;
          }
          return !result;
        }

        if (!f.required || f.value) {
          return false;
        }
        return true;
      })
      .map(f => f.name);
    this.setState({ highlightedFields });
    if (highlightedFields.length) {
      if (messages.length) {
        notificationStore.alert(messages[0], "error");
      }
      return;
    }
    const licensorInput: LicensorInput = inputFieldsSchema.buildData(
      this.state.inputFields,
      this.props.partner,
    );
    if (this.props.licensor) {
      const success = await licensorStore.update(
        this.props.licensor.id,
        licensorInput,
      );
      const loadRoleAssignments = async () => {
        await new Promise(resolve => setTimeout(resolve, 2000));
        roleStore.loadRoleAssignments();
      };
      loadRoleAssignments();
      if (!success) {
        return;
      }
    } else {
      const addedLicensor = await licensorStore.add(licensorInput);
      if (!addedLicensor) {
        return;
      }
      notificationStore.alert(
        "An email has been sent to the new license manager with login instructions.",
        "success",
      );
    }
    this.props.onRequestClose();
  }

  renderButtons() {
    return (
      <Buttons>
        <Button
          secondary
          large
          onClick={this.props.onRequestClose}
          disabled={this.saving}
        >
          Cancel
        </Button>
        {this.saving ? (
          <ButtonLoading large>Saving...</ButtonLoading>
        ) : (
          <Button
            large
            onClick={() => {
              this.handleSaveClick();
            }}
          >
            Save
          </Button>
        )}
      </Buttons>
    );
  }

  renderFields() {
    return (
      <Fields>
        {this.state.inputFields.map(field => {
          return (
            <InputField
              key={field.name}
              field={field}
              disabled={
                this.saving || (field.name === "email" && !!this.props.licensor)
              }
              highlight={
                !!this.state.highlightedFields.find(name => field.name === name)
              }
              onChange={value => {
                this.handleFieldChange(field, value);
              }}
            />
          );
        })}
      </Fields>
    );
  }

  render() {
    return (
      <Modal
        isOpen
        title={
          this.props.licensor ? "Edit Licence Manager" : "New Licence Manager"
        }
        onRequestClose={this.props.onRequestClose}
      >
        <Wrapper>
          <Header>
            <LicensorImage />
          </Header>
          {this.renderFields()}
          {this.renderButtons()}
        </Wrapper>
      </Modal>
    );
  }
}

export default LicensorModal;
